import { useState, useEffect, useRef } from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQCategory {
  title: string;
  items: FAQItem[];
}

export const faqData: FAQCategory[] = [
  {
    title: "Débuter avec Unotif",
    items: [
      {
        question: "Comment envoyer des notifications sans créer d'application ?",
        answer: "UNOTIF utilise Apple Wallet et Google Wallet, des applications déjà présentes sur tous les téléphones. Votre audience ajoute simplement une carte légère en un seul geste (pas d'App Store, pas de téléchargement). Vous pouvez ensuite envoyer des notifications qui s'affichent directement sur l'écran verrouillé. C'est la solution la plus simple pour envoyer des notifications, sans développement."
      },
      {
        question: "Unotif remplace-t-il les SMS ?",
        answer: "Oui, et c'est même mieux. Contrairement aux SMS, Unotif vous permet d'envoyer un nombre illimité de notifications sans frais par message, sans limite de caractères et sans restrictions opérateur. Vos messages apparaissent comme des notifications natives avec un meilleur format, et vous créez un canal de communication qui ne peut pas être bloqué par les filtres anti-spam. C'est plus économique et plus fiable."
      },
      {
        question: "C'est quoi un canal de communication directe ?",
        answer: "C'est un lien direct avec votre audience que vous contrôlez entièrement. Pas d'algorithme, pas de plateforme tierce, pas de risque de perdre l'accès. Avec Unotif, quand quelqu'un ajoute votre carte à son Wallet, vous avez une connexion permanente et directe vers son écran verrouillé. Contrairement aux réseaux sociaux (contrôlés par des algorithmes) ou aux emails (filtrés comme spam), votre canal directe et propriétaire garantit que votre message arrive à destination."
      }
    ]
  },
  {
    title: "Technique et délivrabilité",
    items: [
      {
        question: "Mes abonnés doivent-ils télécharger une application ?",
        answer: "Non, c'est justement l'un des avantages d'Unotif. Les utilisateurs ajoutent simplement la carte à l'application Wallet déjà installée sur leur iPhone (Apple Wallet) ou Android (Google Wallet). Aucune friction, aucun passage par un store."
      },
      {
        question: "Comment mes abonnés ajoutent-ils la carte ?",
        answer: "C'est très simple. Ils scannent un QR code ou cliquent sur un lien que vous leur donnez. La carte s'installe en un geste, et ils sont immédiatement abonnés à vos notifications."
      },
      {
        question: "Quels systèmes sont compatibles ?",
        answer: "UNOTIF fonctionne sur Apple Wallet (iOS) et Google Wallet (Android). Une seule solution pour toucher quasiment toute votre audience mobile, sans écrire une ligne de code."
      },
      {
        question: "Les notifications sont-elles plus fiables que les emails ou les réseaux sociaux ?",
        answer: "Beaucoup plus fiables. Les notifications Wallet contournent les filtres anti-spam, les boîtes mail saturées et les algorithmes des réseaux sociaux. Elles arrivent directement sur l'écran verrouillé. C'est la meilleure visibilité possible, vous êtes certain que votre message important a été vu."
      },
      {
        question: "Peut-on ajouter des QR codes, des liens ou du contenu enrichi ?",
        answer: "Oui. Vous pouvez intégrer des QR codes uniques (pour des réductions ou des check-ins par exemple). Les cartes supportent aussi du texte enrichi et des liens directs vers vos contenus, votre boutique en ligne ou votre espace membre."
      }
    ]
  },
  {
    title: "Stratégie et usage",
    items: [
      {
        question: "Pourquoi utiliser Unotif plutôt que mes outils actuels (emailing, SMS) ?",
        answer: "Unotif ne remplace pas vos outils, il les complète. Vos canaux actuels font face à des obstacles techniques qui tuent la visibilité (spam, algorithmes). Unotif garantit une connexion visible avec vos clients ou abonnés les plus engagés, assurant que vos messages importants sont toujours vus et améliorant les résultats de toute votre stratégie de communication."
      },
      {
        question: "Ce canal va-t-il perdre en efficacité avec le temps ?",
        answer: "C'est un actif durable que vous possédez. Contrairement aux réseaux sociaux, où la portée diminue à mesure que les plateformes se monétisent, ou aux emails, dont les taux d'ouverture s'effondrent, Unotif reste efficace dans le temps. Pourquoi ? Parce que les utilisateurs choisissent activement d'ajouter votre carte lorsqu'il y a une valeur claire en échange. Ce n'est pas un canal imposé, mais un canal choisi. Et puisque vous en avez le contrôle total, personne ne peut en dégrader la performance."
      },
      {
        question: "Peut-on segmenter et personnaliser les messages ?",
        answer: "Oui. Unotif vous permet de segmenter votre audience avec des tags avant l'installation et d'envoyer des notifications ciblées à des groupes spécifiques. Parfait pour faire de l'A/B testing, créer des segments VIP, annoncer des événements réservés à certains membres, ou adapter vos messages selon les préférences de vos abonnés. Vous gardez la simplicité de la diffusion tout en ayant la puissance de la segmentation."
      }
    ]
  },
  {
    title: "Sécurité et données",
    items: [
      {
        question: "Unotif collecte-t-il des données personnelles ou l'historique de localisation ?",
        answer: "Non. Unotif respecte la vie privée. Nous ne collectons ni noms, ni emails, ni données personnelles. Nous suivons uniquement la diffusion des cartes, c'est notre seule métrique. Votre audience reste privée, et toute communication est uniquement en mode diffusion."
      }
    ]
  }
];

const FAQCategoryCard = ({ category }: { category: FAQCategory }) => {
  return (
    <div 
      className="p-6 sm:p-8 rounded-2xl bg-white border border-gray-100 
        shadow-[0_4px_20px_-4px_rgba(0,0,0,0.08),0_2px_8px_-2px_rgba(0,0,0,0.04)]"
    >
      <h3 className="text-lg sm:text-xl font-bold text-[#022226] mb-3">
        {category.title}
      </h3>
      
      <Accordion type="single" collapsible className="w-full">
        {category.items.map((item, index) => (
          <AccordionItem 
            key={index} 
            value={`item-${index}`}
            className="border-b border-gray-100 last:border-b-0"
          >
            <AccordionTrigger className="text-left text-[#022226] hover:no-underline py-4 text-base font-semibold">
              {item.question}
            </AccordionTrigger>
            <AccordionContent className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] pb-4">
              {item.answer}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
};

export const FAQ = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (!isMounted) return;
    if (!contentRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    observer.observe(contentRef.current);
    return () => observer.disconnect();
  }, [isMounted]);

  return (
    <section id="faq" className="pt-[50px] sm:pt-[62.5px] lg:pt-[70px] pb-16 sm:pb-20 lg:pb-24 bg-[#F8F9FA]">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        <div 
          ref={contentRef}
          className={`transition-all duration-700 ease-out ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {/* Header */}
          <div className="text-center mb-12 sm:mb-16">
            {/* Badge */}
            <div className="flex justify-center mb-6">
              <span className="flex items-center gap-2 px-4 py-2 rounded-full border border-[#074155] bg-white text-sm font-semibold text-[#074155]">
                FAQs
              </span>
            </div>
            
            {/* Titre */}
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#074155] mb-4">
              Des questions ?{" "}
              <span className="text-[#0C94A6]">On vous répond.</span>
            </h2>
            
            {/* Phrase intro */}
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] max-w-3xl mx-auto">
              Tout ce qu'il faut savoir sur la carte Wallet comme nouveau canal de communication.
            </p>
          </div>

          {/* FAQ Cards Grid */}
          <div className="flex flex-col gap-6 max-w-4xl mx-auto">
            {faqData.map((category, index) => (
              <FAQCategoryCard key={index} category={category} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
